import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';

import { Resource } from '../../../common/constants/resource';
import { HttpService } from '../../../common/http.service';
import {
  ISummary,
  summaryListActions,
} from '../../tasks/slice/aiSummaryListSlice';
import {
  PromptType,
  TaskTypes,
  TaskTypeToTaskTitleMapping,
} from '../../tasks/types/taskTypes';
import Accurate from './Accurate';
import MarkdownRenderer from './MarkdownRenderer';
import Reason from './Reason';
import TimeSaved from './TimeSaved';

export default function AsyncAccordion({ evidence }: { evidence: ISummary }) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const taskType = evidence.evidenceType as TaskTypes;

  async function onEvidenceClick() {
    setExpanded(!expanded);

    // Skip JSON file processing for EHR and LabPiQture - they only need PDF summaries
    if (
      evidence.evidenceType === TaskTypes.EHR ||
      evidence.evidenceType === TaskTypes.LAB_PIQTURE
    ) {
      return;
    }

    if (!isLoading && !evidence.summary) {
      setLoading(true);
      const raw = {
        applicationId: id,
        evidenceId: evidence.id,
        evidenceType: evidence.evidenceType,
        fileKey: `${evidence.carrier}/${evidence.product}/${evidence.evidenceType}/${evidence.evidenceTracker}.evidence.json`,
        promptType: evidence.promptType,
      };
      const url = `automatedEvidences/summary/info`;
      try {
        const response = await HttpService.getInstance().post(url, raw);
        dispatch(
          summaryListActions.updateSummary({
            ...evidence,
            ...response.parsedBody,
          }),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }

  const titlePostfixStr =
    evidence.promptType === PromptType.GridView
      ? ` ${Resource.TABULAR_VIEW}`
      : '';

  return (
    <Accordion expanded={expanded} onChange={onEvidenceClick}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id={evidence.evidenceType}
      >
        <Typography>
          {TaskTypeToTaskTitleMapping[taskType] + titlePostfixStr}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {/* Show appropriate content based on evidence type */}
            {evidence.evidenceType === TaskTypes.EHR ||
            evidence.evidenceType === TaskTypes.LAB_PIQTURE ? (
              <Typography variant="body2" color="text.secondary">
                {evidence.evidenceType === TaskTypes.EHR
                  ? 'EHR evidence summary will be available once PDF documents are processed.'
                  : 'LabPiQture evidence summary will be available once PDF documents are processed.'}
              </Typography>
            ) : (
              <>
                <MarkdownRenderer text={evidence.summary} />
                <FeedbackContainer elevation={3} variant="outlined">
                  <Accurate {...evidence} />
                  {evidence.like === true && <TimeSaved {...evidence} />}
                  {evidence.like === false && <Reason {...evidence} />}
                </FeedbackContainer>
              </>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const FeedbackContainer = styled(Paper)(() => ({
  backgroundColor: '#E6E6E6',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  padding: '15px',
  marginTop: '15px',
}));
